import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Save, Database, Power, X, ChevronDown, AlertCircle, CheckCircle, Pencil, Activity, Zap } from "lucide-react";
import Header from "../components/layout/Header";
import { Modal } from "../components/ui/index";
import { noeudsAPI, fonctionsAPI, automatisationsAPI } from "../api/services";

const analyses = ["Moyenne", "Somme", "Minimum", "Maximum", "Écart-type", "Tendance", "Prédiction"];
const analysisKeyMap = { "Moyenne": "average", "Somme": "sum", "Minimum": "minimum", "Maximum": "maximum", "Écart-type": "stddev", "Tendance": "trend", "Prédiction": "prediction" };

const periodeValues = [
  { value: "1h" },
  { value: "6h" },
  { value: "12h" },
  { value: "1d" },
  { value: "3d" },
  { value: "7d" },
  { value: "14d" },
  { value: "30d" },
  { value: "90d" },
];

const conditionValues = [">", "<", "=", ">=", "<=", "≠"];

export default function CreerFonction() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id") || "";
  const projetId = searchParams.get("projet") || "";
  const revendeurId = searchParams.get("revendeur") || "";
  const clientId = searchParams.get("client") || "";
  const isEdit = !!editId;

  const [nom, setNom] = useState("");
  const [description, setDescription] = useState("");
  const [cooldown, setCooldown] = useState(30);
  const [capteurs, setCapteurs] = useState([]);
  const [actionneurs, setActionneurs] = useState([]);
  const [showCapteurDD, setShowCapteurDD] = useState(false);
  const [showActionneurDD, setShowActionneurDD] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Modal capteur config
  const [capteurModal, setCapteurModal] = useState(null); // { id, label, type, analyse, periode, condition, valeur, isNew }
  // Modal actionneur config
  const [actionneurModal, setActionneurModal] = useState(null); // { id, label, type, action, isNew }

  // Noeuds disponibles du projet
  const [availCapteurs, setAvailCapteurs] = useState([]);
  const [availActionneurs, setAvailActionneurs] = useState([]);
  const [loadingNoeuds, setLoadingNoeuds] = useState(false);

  // Automatisations du projet (pour mise en pause configurable)
  const [projectAutos, setProjectAutos] = useState([]);
  const [pauseAutoIds, setPauseAutoIds] = useState([]);

  // Helper functions for translated labels
  const getAnalysisLabel = (value) => t(`createFunction.analysisTypes.${analysisKeyMap[value]}`) || value;
  const getPeriodeLabel = (value) => t(`createFunction.periodLabels.${value}`) || value;
  const getConditionLabel = (value) => t(`createFunction.conditionLabels.${value}`) || value;

  // Charger les noeuds du projet
  useEffect(() => {
    if (!projetId) return;
    setLoadingNoeuds(true);
    noeudsAPI.getAll({ projet: projetId })
      .then(res => {
        const list = res.data?.data || res.data || [];
        setAvailCapteurs(list.filter(n => n.categorie === "Capteur").map(n => ({ id: n.sn, label: `${n.sn} — ${n.type}`, type: n.type })));
        setAvailActionneurs(list.filter(n => n.categorie === "Actionneur").map(n => ({ id: n.sn, label: `${n.sn} — ${n.type}`, type: n.type })));
      })
      .catch(err => console.error("Erreur chargement noeuds:", err))
      .finally(() => setLoadingNoeuds(false));
  }, [projetId]);

  // Charger les automatisations actives du projet
  useEffect(() => {
    if (!projetId) { setProjectAutos([]); return; }
    automatisationsAPI.getAll({ projet: projetId })
      .then(res => {
        const autos = (res.data?.data || []).filter(a => a.statut === 'Actif');
        setProjectAutos(autos);
      })
      .catch(() => setProjectAutos([]));
  }, [projetId]);

  // Charger la fonction existante en mode édition
  useEffect(() => {
    if (!editId) return;
    fonctionsAPI.getOne(editId)
      .then(res => {
        const f = res.data?.data || res.data;
        if (f) {
          setNom(f.nom || "");
          setDescription(f.description || "");
          setCooldown(typeof f.cooldown === "number" ? f.cooldown : 30);
          if (f.capteurs) setCapteurs(f.capteurs.map(c => ({
            ...c,
            analyse: c.analyse || "Moyenne",
            periode: c.periode || "1d",
            condition: c.condition || ">",
            valeur: c.valeur ?? 0
          })));
          if (f.actionneurs) setActionneurs(f.actionneurs.map(a => ({ ...a, action: a.action || "Activer" })));
          if (f.pauseAutomatisations) {
            setPauseAutoIds(f.pauseAutomatisations.map(a => a._id || a));
          }
        }
      })
      .catch(err => console.error("Erreur chargement fonction:", err));
  }, [editId]);

  // Ouvrir la modal pour configurer un nouveau capteur
  const openNewCapteurModal = (c) => {
    setCapteurModal({ ...c, analyse: "Moyenne", periode: "1d", condition: ">", valeur: "", isNew: true });
    setShowCapteurDD(false);
  };

  // Ouvrir la modal pour éditer un capteur existant
  const openEditCapteurModal = (c) => {
    setCapteurModal({ ...c, isNew: false });
  };

  // Sauvegarder le capteur depuis la modal
  const saveCapteurModal = () => {
    if (!capteurModal || capteurModal.valeur === "" || capteurModal.valeur === undefined) return;
    const data = { id: capteurModal.id, label: capteurModal.label, type: capteurModal.type, analyse: capteurModal.analyse, periode: capteurModal.periode, condition: capteurModal.condition, valeur: parseFloat(capteurModal.valeur) };
    if (capteurModal.isNew) {
      setCapteurs(prev => [...prev, data]);
    } else {
      setCapteurs(prev => prev.map(c => c.id === data.id ? data : c));
    }
    setCapteurModal(null);
  };

  // Ouvrir la modal pour configurer un nouvel actionneur
  const openNewActionneurModal = (a) => {
    setActionneurModal({ ...a, action: "Activer", isNew: true });
    setShowActionneurDD(false);
  };

  // Ouvrir la modal pour éditer un actionneur existant
  const openEditActionneurModal = (a) => {
    setActionneurModal({ ...a, isNew: false });
  };

  // Sauvegarder l'actionneur depuis la modal
  const saveActionneurModal = () => {
    if (!actionneurModal) return;
    const data = { id: actionneurModal.id, label: actionneurModal.label, type: actionneurModal.type, action: actionneurModal.action };
    if (actionneurModal.isNew) {
      setActionneurs(prev => [...prev, data]);
    } else {
      setActionneurs(prev => prev.map(a => a.id === data.id ? data : a));
    }
    setActionneurModal(null);
  };

  const removeCapteur = (id) => setCapteurs(prev => prev.filter(c => c.id !== id));
  const removeActionneur = (id) => setActionneurs(prev => prev.filter(a => a.id !== id));

  // Pré-cocher les automatisations impactées quand les actionneurs changent.
  // En mode édition : skip — le state sauvegardé est la source de vérité.
  useEffect(() => {
    if (isEdit) return;
    if (!projectAutos.length || actionneurs.length === 0) return;

    const targetSns = actionneurs.map(a => a.id);
    const suggestedIds = projectAutos
      .filter(auto => {
        const autoActSns = (auto.actionneurs || []).map(a => a.sn || a);
        return autoActSns.some(sn => targetSns.includes(sn));
      })
      .map(a => a._id);

    setPauseAutoIds(prev => [...new Set([...prev, ...suggestedIds])]);
  }, [actionneurs, projectAutos, isEdit]);

  const erreurs = [];
  if (!nom.trim()) erreurs.push(t('createFunction.nameRequired'));
  if (capteurs.length === 0) erreurs.push(t('createFunction.addAtLeastOneSensorValidation'));
  const isValid = erreurs.length === 0;

  const handleSave = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    try {
      const payload = {
        nom,
        description,
        projet: projetId,
        revendeur: revendeurId || undefined,
        client: clientId || undefined,
        capteurs: capteurs.map(c => ({ id: c.id, label: c.label, type: c.type, analyse: c.analyse, periode: c.periode, condition: c.condition, valeur: c.valeur })),
        actionneurs: actionneurs.map(a => ({ id: a.id, label: a.label, type: a.type, action: a.action })),
        pauseAutomatisations: pauseAutoIds,
        cooldown: Number.isFinite(Number(cooldown)) ? Math.max(0, Number(cooldown)) : 30,
        statut: "Actif",
      };
      if (isEdit) {
        await fonctionsAPI.update(editId, payload);
        setToast({ type: "success", message: t('createFunction.successUpdated') });
      } else {
        await fonctionsAPI.create(payload);
        setToast({ type: "success", message: t('createFunction.successCreated') });
      }
      setTimeout(() => navigate("/fonctions"), 1500);
    } catch (err) {
      console.error("Erreur sauvegarde fonction:", err);
      setToast({ type: "error", message: err.response?.data?.error || t('createFunction.saveError') });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500";
  const sectionCard = "bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-4";

  const unusedCapteurs = availCapteurs.filter(c => !capteurs.find(x => x.id === c.id));
  const unusedActionneurs = availActionneurs.filter(a => !actionneurs.find(x => x.id === a.id));

  // Aperçu langage naturel
  const buildPreview = () => {
    if (capteurs.length === 0) return null;
    const capteurParts = capteurs.map(c => {
      const analyseLC = getAnalysisLabel(c.analyse).toLowerCase();
      return `${c.type} ${c.id} a fait une ${analyseLC} ${c.condition} ${c.valeur} pendant ${getPeriodeLabel(c.periode)}`;
    });
    const conditionText = capteurParts.length > 1
      ? capteurParts.join(" ET ")
      : capteurParts[0];

    const actionParts = actionneurs.map(a => `${a.action === "Activer" ? t('createFunction.enable') : t('createFunction.disable')} (${a.type} ${a.id})`);
    const actionText = actionParts.length > 0
      ? actionParts.join(" et ")
      : "aucune action définie";

    return { conditionText, actionText };
  };

  const preview = buildPreview();

  return (
    <>
      <Header title={t('functions.title')} subtitle={t('functions.subtitle')} />

      {/* Header création */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/fonctions")}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50">
            <ArrowLeft size={16} /> {t('createFunction.back')}
          </button>
          <div>
            <h1 className="font-bold text-slate-800 text-lg">{isEdit ? t('createFunction.editTitle') : t('createFunction.createTitle')}</h1>
            <p className="text-sm text-slate-400">{t('createFunction.project')} {projetId}</p>
          </div>
        </div>
        <button
          disabled={!isValid || saving}
          onClick={handleSave}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            isValid && !saving
              ? "bg-teal-600 text-white hover:bg-teal-700"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }`}
        >
          <Save size={16} /> {saving ? t('createFunction.saving') : isEdit ? t('createFunction.update') : t('createFunction.save')}
        </button>
      </div>

      {/* Nom + Description */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            {t('createFunction.nameLabel')} <span className="text-red-400">*</span>
          </label>
          <input value={nom} onChange={e => setNom(e.target.value)}
            placeholder="Ex: Contrôle Irrigation Trimestriel"
            className={inputStyle} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            {t('createFunction.descriptionLabel')}
          </label>
          <input value={description} onChange={e => setDescription(e.target.value)}
            placeholder="Description personnalisée..."
            className={inputStyle} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Cooldown (minutes)
          </label>
          <input
            type="number"
            min={0}
            value={cooldown}
            onChange={e => setCooldown(e.target.value === "" ? "" : Number(e.target.value))}
            className={inputStyle}
          />
          <p className="text-xs text-slate-400 mt-1">
            Délai minimum entre deux déclenchements. 0 = aucun (la fonction peut firer à chaque cycle de 5 min).
          </p>
        </div>
      </div>

      {/* Section Capteurs */}
      <div className={sectionCard}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <Database size={18} className="text-blue-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-800">{t('createFunction.sensorsTitle')}</p>
            <p className="text-xs text-slate-400">{t('createFunction.sensorsSubtitle')}</p>
          </div>
        </div>

        {/* Dropdown capteurs */}
        <div className="relative mb-4">
          <button
            onClick={() => { setShowCapteurDD(!showCapteurDD); setShowActionneurDD(false); }}
            className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-sm text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
          >
            <span className="text-lg font-light">+</span> {t('createFunction.addSensor')}
            <ChevronDown size={14} className="ml-1" />
          </button>
          {showCapteurDD && (
            <div className="absolute left-0 top-12 z-20 bg-white border border-gray-200 rounded-xl shadow-lg py-1 w-80 max-h-60 overflow-y-auto">
              {loadingNoeuds ? (
                <p className="px-4 py-3 text-sm text-slate-400">{t('common.loading')}</p>
              ) : unusedCapteurs.length > 0 ? unusedCapteurs.map(c => (
                <button key={c.id} onClick={() => openNewCapteurModal(c)}
                  className="w-full px-4 py-2.5 text-left hover:bg-gray-50 text-sm text-slate-700">
                  <p className="font-medium">{c.id}</p>
                  <p className="text-xs text-slate-400">{c.type}</p>
                </button>
              )) : (
                <p className="px-4 py-3 text-sm text-slate-400">
                  {availCapteurs.length === 0 ? t('createFunction.noSensorsInProject') : t('createFunction.allSensorsAdded')}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Liste capteurs sélectionnés */}
        {capteurs.length > 0 ? (
          <div className="space-y-3">
            {capteurs.map(c => (
              <div key={c.id} className="rounded-xl border-2 border-blue-200 bg-blue-50 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Activity size={16} className="text-blue-500" />
                    <span className="text-sm font-semibold text-slate-800">{c.id}</span>
                    <span className="text-xs bg-blue-200 text-blue-700 px-2 py-0.5 rounded-full">{c.type}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEditCapteurModal(c)} className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors">
                      <Pencil size={14} className="text-blue-500" />
                    </button>
                    <button onClick={() => removeCapteur(c.id)} className="p-1.5 hover:bg-red-100 rounded-lg transition-colors">
                      <X size={14} className="text-slate-400 hover:text-red-500" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-white rounded-lg px-3 py-2 border border-blue-100">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">{t('createFunction.functionType')}</p>
                    <p className="text-sm font-medium text-slate-700">{getAnalysisLabel(c.analyse)}</p>
                  </div>
                  <div className="bg-white rounded-lg px-3 py-2 border border-blue-100">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">{t('createFunction.analysisPeriod')}</p>
                    <p className="text-sm font-medium text-slate-700">{getPeriodeLabel(c.periode)}</p>
                  </div>
                  <div className="bg-white rounded-lg px-3 py-2 border border-blue-100">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">{t('createFunction.condition')}</p>
                    <p className="text-sm font-medium text-orange-600">{c.condition} {c.valeur}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-200 rounded-xl p-12 text-center">
            <Database size={40} className="text-gray-200 mx-auto mb-3" />
            <p className="text-slate-400 font-medium">{t('createFunction.noSensorSelected')}</p>
            <p className="text-slate-300 text-sm mt-1">{t('createFunction.addAtLeastOneSensor')}</p>
          </div>
        )}
      </div>

      {/* Section Actionneurs */}
      <div className={sectionCard}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
            <Power size={18} className="text-orange-500" />
          </div>
          <div>
            <p className="font-semibold text-slate-800">{t('createFunction.actuatorsTitle')}</p>
            <p className="text-xs text-slate-400">{t('createFunction.actuatorsSubtitle')}</p>
          </div>
        </div>

        {/* Dropdown actionneurs */}
        <div className="relative mb-4">
          <button
            onClick={() => { setShowActionneurDD(!showActionneurDD); setShowCapteurDD(false); }}
            className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-sm text-slate-500 hover:border-orange-400 hover:text-orange-600 transition-colors"
          >
            <span className="text-lg font-light">+</span> {t('createFunction.addActuator')}
            <ChevronDown size={14} className="ml-1" />
          </button>
          {showActionneurDD && (
            <div className="absolute left-0 top-12 z-20 bg-white border border-gray-200 rounded-xl shadow-lg py-1 w-80 max-h-60 overflow-y-auto">
              {loadingNoeuds ? (
                <p className="px-4 py-3 text-sm text-slate-400">{t('common.loading')}</p>
              ) : unusedActionneurs.length > 0 ? unusedActionneurs.map(a => (
                <button key={a.id} onClick={() => openNewActionneurModal(a)}
                  className="w-full px-4 py-2.5 text-left hover:bg-gray-50 text-sm text-slate-700">
                  <p className="font-medium">{a.id}</p>
                  <p className="text-xs text-slate-400">{a.type}</p>
                </button>
              )) : (
                <p className="px-4 py-3 text-sm text-slate-400">
                  {availActionneurs.length === 0 ? t('createFunction.noActuatorsInProject') : t('createFunction.allActuatorsAdded')}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Liste actionneurs sélectionnés */}
        {actionneurs.length > 0 ? (
          <div className="space-y-3">
            {actionneurs.map(a => (
              <div key={a.id} className="rounded-xl border-2 border-orange-200 bg-orange-50 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Zap size={16} className="text-orange-500" />
                    <span className="text-sm font-semibold text-slate-800">{a.id}</span>
                    <span className="text-xs bg-orange-200 text-orange-700 px-2 py-0.5 rounded-full">{a.type}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="bg-white rounded-lg px-3 py-1.5 border border-orange-100">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider">Action</p>
                      <p className={`text-sm font-medium ${a.action === "Désactiver" ? "text-red-600" : "text-green-600"}`}>
                        {a.action === "Désactiver" ? t('createFunction.disable') : t('createFunction.enable')}
                      </p>
                    </div>
                    <button onClick={() => openEditActionneurModal(a)} className="p-1.5 hover:bg-orange-100 rounded-lg transition-colors">
                      <Pencil size={14} className="text-orange-500" />
                    </button>
                    <button onClick={() => removeActionneur(a.id)} className="p-1.5 hover:bg-red-100 rounded-lg transition-colors">
                      <X size={14} className="text-slate-400 hover:text-red-500" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-200 rounded-xl p-12 text-center">
            <Power size={40} className="text-gray-200 mx-auto mb-3" />
            <p className="text-slate-400 font-medium">{t('createFunction.noActuatorSelected')}</p>
            <p className="text-slate-300 text-sm mt-1">{t('createFunction.actuatorsOptional')}</p>
          </div>
        )}
      </div>

      {/* Section : Automatisations à mettre en pause */}
      {projectAutos.length > 0 && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-4">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
              <Zap size={20} className="text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">{t('createFunction.pauseAutomationsTitle', 'Mettre en pause des automatisations')}</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {t('createFunction.pauseAutomationsDesc', 'Quand cette fonction se déclenche, les automatisations cochées seront mises en pause automatiquement. Un redémarrage manuel sera nécessaire depuis la page Automatisations.')}
              </p>
            </div>
            {pauseAutoIds.length > 0 && (
              <span className="ml-auto px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-lg text-xs font-bold text-amber-700 shrink-0">
                {pauseAutoIds.length} en pause
              </span>
            )}
          </div>
          <div className="space-y-2">
            {projectAutos.map(auto => {
              const isChecked = pauseAutoIds.includes(auto._id);
              const actNames = (auto.actionneurs || []).map(a => a.sn || a).join(', ');
              const creneauxDesc = (auto.creneaux || []).map(c => `${c.debut}→${c.fin}`).join(', ');

              // Vérifier si cette auto est directement impactée (a un actionneur en commun avec la fonction)
              const fnActSns = actionneurs.map(a => a.id);
              const autoActSns = (auto.actionneurs || []).map(a => a.sn || a);
              const isImpacted = fnActSns.some(sn => autoActSns.includes(sn));

              return (
                <label
                  key={auto._id}
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    isChecked
                      ? 'border-amber-400 bg-amber-50'
                      : 'border-gray-100 bg-gray-50 hover:border-gray-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => setPauseAutoIds(prev =>
                      prev.includes(auto._id)
                        ? prev.filter(id => id !== auto._id)
                        : [...prev, auto._id]
                    )}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-800 text-sm">{auto.nom}</p>
                      {isImpacted && (
                        <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-bold">{t('createFunction.impacted', 'Impactée')}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      <span className="text-[11px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-md">{actNames}</span>
                      {creneauxDesc && (
                        <span className="text-[11px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md">{creneauxDesc}</span>
                      )}
                    </div>
                  </div>
                  {isChecked && (
                    <span className="text-[11px] bg-amber-200 text-amber-800 px-2 py-1 rounded-md font-medium shrink-0">
                      {t('createFunction.willBePaused', 'Sera mise en pause')}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
          <div className="flex gap-3 mt-3 pt-3 border-t border-gray-100">
            <button
              onClick={() => setPauseAutoIds(projectAutos.map(a => a._id))}
              className="text-xs text-amber-700 hover:text-amber-900 font-medium underline"
            >
              {t('createFunction.checkAll', 'Tout cocher')}
            </button>
            <button
              onClick={() => setPauseAutoIds([])}
              className="text-xs text-slate-400 hover:text-slate-600 font-medium underline"
            >
              {t('createFunction.uncheckAll', 'Tout décocher')}
            </button>
          </div>
        </div>
      )}

      {/* Aperçu en langage naturel */}
      {preview && (
        <div className="bg-teal-50 border-2 border-teal-200 rounded-2xl p-5 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
              <Activity size={16} className="text-teal-600" />
            </div>
            <p className="font-semibold text-slate-800">{t('createFunction.preview')}</p>
            <span className="text-[10px] bg-teal-600 text-white px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold">{t('createFunction.naturalLanguage')}</span>
          </div>
          <div className="bg-white rounded-xl p-4 border border-teal-100">
            <p className="text-sm text-slate-700 leading-relaxed">
              <span className="font-semibold text-teal-700">{t('createFunction.if')} </span>
              <span className="text-blue-600 font-medium">{preview.conditionText}</span>
            </p>
            <p className="text-sm text-slate-700 mt-1">
              <span className="font-semibold text-orange-600">{t('createFunction.then')} </span>
              <span className="font-medium">{preview.actionText}</span>
            </p>
          </div>
          <div className="flex gap-2 mt-3">
            <span className="text-[11px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md">{capteurs.length} capteur{capteurs.length > 1 ? "s" : ""}</span>
            <span className="text-[11px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-md">{actionneurs.length} actionneur{actionneurs.length > 1 ? "s" : ""}</span>
          </div>
        </div>
      )}

      {/* Banner validation */}
      {!isValid && (
        <div className="rounded-2xl p-4 bg-yellow-50 border border-yellow-200 flex items-start gap-3">
          <AlertCircle size={18} className="text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-yellow-800">{t('createFunction.incompleteTitle')}</p>
            <ul className="mt-1 space-y-0.5">
              {erreurs.map(e => (
                <li key={e} className="text-sm text-yellow-700">• {e}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Modal configuration capteur */}
      <Modal
        isOpen={!!capteurModal}
        onClose={() => setCapteurModal(null)}
        title={t('createFunction.configureSensor')}
        subtitle={capteurModal ? `${capteurModal.id} - ${capteurModal.type}` : ""}
        size="sm"
      >
        {capteurModal && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('createFunction.functionType')} *</label>
              <select
                value={capteurModal.analyse}
                onChange={e => setCapteurModal(prev => ({ ...prev, analyse: e.target.value }))}
                className={inputStyle}
              >
                {analyses.map(a => <option key={a} value={a}>{getAnalysisLabel(a)}</option>)}
              </select>
              <p className="text-xs text-blue-500 mt-1">{t('createFunction.configureSensorSubtitle')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('createFunction.analysisPeriod')} *</label>
              <select
                value={capteurModal.periode}
                onChange={e => setCapteurModal(prev => ({ ...prev, periode: e.target.value }))}
                className={inputStyle}
              >
                {periodeValues.map(p => <option key={p.value} value={p.value}>{getPeriodeLabel(p.value)}</option>)}
              </select>
              <p className="text-xs text-blue-500 mt-1">{t('createFunction.analysisDuration')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('createFunction.condition')} *</label>
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={capteurModal.condition}
                  onChange={e => setCapteurModal(prev => ({ ...prev, condition: e.target.value }))}
                  className={inputStyle}
                >
                  {conditionValues.map(c => <option key={c} value={c}>{getConditionLabel(c)}</option>)}
                </select>
                <input
                  type="number"
                  step="any"
                  value={capteurModal.valeur}
                  onChange={e => setCapteurModal(prev => ({ ...prev, valeur: e.target.value }))}
                  placeholder="Ex: 20, 45%, 30.5"
                  className={inputStyle}
                />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-1">
                <p className="text-xs text-blue-500">{t('createFunction.comparator')}</p>
                <p className="text-xs text-blue-500">{t('createFunction.value')}</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
              <button onClick={() => setCapteurModal(null)}
                className="px-4 py-2 bg-gray-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors">
                {t('createFunction.cancel')}
              </button>
              <button
                onClick={saveCapteurModal}
                disabled={capteurModal.valeur === "" || capteurModal.valeur === undefined}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  capteurModal.valeur !== "" && capteurModal.valeur !== undefined
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                <Save size={14} /> {t('createFunction.add')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal configuration actionneur */}
      <Modal
        isOpen={!!actionneurModal}
        onClose={() => setActionneurModal(null)}
        title={t('createFunction.configureActuator')}
        subtitle={actionneurModal ? `${actionneurModal.id} - ${actionneurModal.type}` : ""}
        size="sm"
      >
        {actionneurModal && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('createFunction.actionRequired')}</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setActionneurModal(prev => ({ ...prev, action: "Activer" }))}
                  className={`rounded-xl py-4 flex flex-col items-center gap-2 border-2 transition-all ${
                    actionneurModal.action === "Activer"
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <Power size={20} className={actionneurModal.action === "Activer" ? "text-green-500" : "text-slate-400"} />
                  <span className={`text-sm font-semibold ${actionneurModal.action === "Activer" ? "text-green-700" : "text-slate-500"}`}>{t('createFunction.enable')}</span>
                </button>
                <button
                  onClick={() => setActionneurModal(prev => ({ ...prev, action: "Désactiver" }))}
                  className={`rounded-xl py-4 flex flex-col items-center gap-2 border-2 transition-all ${
                    actionneurModal.action === "Désactiver"
                      ? "border-red-500 bg-red-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <Power size={20} className={actionneurModal.action === "Désactiver" ? "text-red-500" : "text-slate-400"} />
                  <span className={`text-sm font-semibold ${actionneurModal.action === "Désactiver" ? "text-red-700" : "text-slate-500"}`}>{t('createFunction.disable')}</span>
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
              <button onClick={() => setActionneurModal(null)}
                className="px-4 py-2 bg-gray-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors">
                {t('createFunction.cancel')}
              </button>
              <button
                onClick={saveActionneurModal}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-xl text-sm font-medium hover:bg-orange-700 transition-colors"
              >
                <Save size={14} /> {t('createFunction.add')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Click outside dropdowns */}
      {(showCapteurDD || showActionneurDD) && (
        <div className="fixed inset-0 z-10" onClick={() => { setShowCapteurDD(false); setShowActionneurDD(false); }} />
      )}

      {/* Toast notification */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg text-sm font-medium ${
          toast.type === "success" ? "bg-green-600 text-white" : "bg-red-600 text-white"
        }`}>
          {toast.type === "success" ? <CheckCircle size={18} /> : <X size={18} />}
          {toast.message}
        </div>
      )}
    </>
  );
}
